import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Switch } from '../../components/ui';
import TodosMonthlyView from './TodosMonthlyView';
import TodosWeeklyView from './TodosWeeklyView';
import TodosDailyView from './TodosDailyView';

dayjs.extend(isoWeek);

export type TodosCalendarViewMode = 'month' | 'week' | 'day';

const STORAGE_KEY = 'todos-calendar-view';

type StoredState = {
  viewMode: TodosCalendarViewMode;
  date: string;
  showOnlyOpen: boolean;
  showAppointments: boolean;
};

function readStoredState(): Partial<StoredState> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<StoredState>;
    if (parsed.viewMode && !['month', 'week', 'day'].includes(parsed.viewMode)) delete parsed.viewMode;
    if (parsed.date && !dayjs(parsed.date).isValid()) delete parsed.date;
    return parsed;
  } catch {
    return {};
  }
}

function writeStoredState(s: StoredState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // ignore
  }
}

const TodosCalendarView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { colors } = useTheme();

  const fromUrl = useCallback((): Partial<StoredState> => {
    const view = searchParams.get('view');
    const date = searchParams.get('date');
    const openOnly = searchParams.get('openOnly');
    const appointments = searchParams.get('appointments');
    const out: Partial<StoredState> = {};
    if (view === 'month' || view === 'week' || view === 'day') out.viewMode = view;
    if (date && dayjs(date).isValid()) out.date = date;
    if (openOnly === '0' || openOnly === '1') out.showOnlyOpen = openOnly === '1';
    if (appointments === '0' || appointments === '1') out.showAppointments = appointments === '1';
    return out;
  }, [searchParams]);

  const [viewMode, setViewModeState] = useState<TodosCalendarViewMode>(() => {
    const url = searchParams.get('view');
    if (url === 'month' || url === 'week' || url === 'day') return url;
    const stored = readStoredState();
    return (stored.viewMode as TodosCalendarViewMode) ?? 'month';
  });
  const [currentDate, setCurrentDateState] = useState<Dayjs>(() => {
    const dateParam = searchParams.get('date');
    if (dateParam && dayjs(dateParam).isValid()) return dayjs(dateParam).startOf('day');
    const stored = readStoredState();
    if (stored.date && dayjs(stored.date).isValid()) return dayjs(stored.date).startOf('day');
    return dayjs().startOf('day');
  });
  const [showAppointments, setShowAppointmentsState] = useState(() => {
    const appointments = searchParams.get('appointments');
    if (appointments === '0' || appointments === '1') return appointments === '1';
    const stored = readStoredState();
    return stored.showAppointments ?? false;
  });
  const [showOnlyOpen, setShowOnlyOpenState] = useState(() => {
    const openOnly = searchParams.get('openOnly');
    if (openOnly === '0' || openOnly === '1') return openOnly === '1';
    const stored = readStoredState();
    return stored.showOnlyOpen ?? true;
  });

  useEffect(() => {
    const url = fromUrl();
    if (url.viewMode) setViewModeState(url.viewMode);
    if (url.date) setCurrentDateState(dayjs(url.date).startOf('day'));
    if (url.showOnlyOpen !== undefined) setShowOnlyOpenState(url.showOnlyOpen);
    if (url.showAppointments !== undefined) setShowAppointmentsState(url.showAppointments);
  }, [searchParams]); // only when URL changes (e.g. browser back)

  useEffect(() => {
    if (!searchParams.get('view') && !searchParams.get('date')) {
      writeStoredState({
        viewMode,
        date: currentDate.format('YYYY-MM-DD'),
        showOnlyOpen,
        showAppointments,
      });
      setSearchParams(
        {
          view: viewMode,
          date: currentDate.format('YYYY-MM-DD'),
          openOnly: showOnlyOpen ? '1' : '0',
          appointments: showAppointments ? '1' : '0',
        },
        { replace: true }
      );
    }
  }, []); // run once on mount to sync URL from state/sessionStorage

  const setViewMode = useCallback(
    (mode: TodosCalendarViewMode | ((prev: TodosCalendarViewMode) => TodosCalendarViewMode)) => {
      setViewModeState((prev) => {
        const next = typeof mode === 'function' ? mode(prev) : mode;
        writeStoredState({
          viewMode: next,
          date: currentDate.format('YYYY-MM-DD'),
          showOnlyOpen,
          showAppointments,
        });
        setSearchParams(
          { view: next, date: currentDate.format('YYYY-MM-DD'), openOnly: showOnlyOpen ? '1' : '0', appointments: showAppointments ? '1' : '0' },
          { replace: true }
        );
        return next;
      });
    },
    [currentDate, showOnlyOpen, showAppointments, setSearchParams]
  );

  const setCurrentDate = useCallback(
    (d: Dayjs | ((prev: Dayjs) => Dayjs)) => {
      setCurrentDateState((prev) => {
        const next = typeof d === 'function' ? d(prev) : d;
        writeStoredState({
          viewMode,
          date: next.format('YYYY-MM-DD'),
          showOnlyOpen,
          showAppointments,
        });
        setSearchParams(
          { view: viewMode, date: next.format('YYYY-MM-DD'), openOnly: showOnlyOpen ? '1' : '0', appointments: showAppointments ? '1' : '0' },
          { replace: true }
        );
        return next;
      });
    },
    [viewMode, showOnlyOpen, showAppointments, setSearchParams]
  );

  const setShowOnlyOpen = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      setShowOnlyOpenState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        writeStoredState({
          viewMode,
          date: currentDate.format('YYYY-MM-DD'),
          showOnlyOpen: next,
          showAppointments,
        });
        setSearchParams(
          { view: viewMode, date: currentDate.format('YYYY-MM-DD'), openOnly: next ? '1' : '0', appointments: showAppointments ? '1' : '0' },
          { replace: true }
        );
        return next;
      });
    },
    [viewMode, currentDate, showAppointments, setSearchParams]
  );

  const setShowAppointments = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      setShowAppointmentsState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        writeStoredState({
          viewMode,
          date: currentDate.format('YYYY-MM-DD'),
          showOnlyOpen,
          showAppointments: next,
        });
        setSearchParams(
          { view: viewMode, date: currentDate.format('YYYY-MM-DD'), openOnly: showOnlyOpen ? '1' : '0', appointments: next ? '1' : '0' },
          { replace: true }
        );
        return next;
      });
    },
    [viewMode, currentDate, showOnlyOpen, setSearchParams]
  );

  const tabs: { id: TodosCalendarViewMode; label: string }[] = [
    { id: 'month', label: 'Month' },
    { id: 'week', label: 'Week' },
    { id: 'day', label: 'Day' },
  ];

  const weekStart = currentDate.startOf('isoWeek');
  const weekEnd = weekStart.add(6, 'day');
  const dateLabel =
    viewMode === 'month'
      ? currentDate.format('MMMM YYYY')
      : viewMode === 'week'
        ? `${weekStart.format('D')}–${weekEnd.format('D MMM YYYY')}`
        : currentDate.format('ddd, D MMM YYYY');

  const handlePrevious = () =>
    setCurrentDate((d) =>
      viewMode === 'month' ? d.subtract(1, 'month') : viewMode === 'week' ? d.subtract(1, 'week') : d.subtract(1, 'day')
    );
  const handleNext = () =>
    setCurrentDate((d) =>
      viewMode === 'month' ? d.add(1, 'month') : viewMode === 'week' ? d.add(1, 'week') : d.add(1, 'day')
    );
  const handleToday = () => setCurrentDate(dayjs().startOf('day'));

  return (
    <div className={cn('flex flex-col h-full', colors.background)}>
      <div
        className={cn(
          'shrink-0 border-b flex flex-nowrap items-center gap-4 px-4 py-3 overflow-x-auto',
          colors.border,
          colors.surfaceHover
        )}
      >
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', colors.info)}>
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <div className="flex rounded-lg border overflow-hidden shrink-0" role="tablist" aria-label="Calendar view">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={viewMode === tab.id}
              onClick={() => setViewMode(tab.id)}
              className={cn(
                'px-4 py-2 text-sm font-medium transition-colors',
                viewMode === tab.id
                  ? cn(colors.primary, colors.primaryText)
                  : cn(colors.text, colors.interactiveHover)
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          <button
            onClick={handlePrevious}
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors',
              colors.border,
              colors.interactiveHover
            )}
            aria-label={viewMode === 'month' ? 'Previous month' : viewMode === 'week' ? 'Previous week' : 'Previous day'}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className={cn('min-w-[120px] sm:min-w-[160px] rounded-lg border px-3 py-2 text-center shrink-0', colors.border, colors.surface)}>
            <span className={cn('text-sm font-semibold whitespace-nowrap', colors.text)}>{dateLabel}</span>
          </div>
          <button
            onClick={handleNext}
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors',
              colors.border,
              colors.interactiveHover
            )}
            aria-label={viewMode === 'month' ? 'Next month' : viewMode === 'week' ? 'Next week' : 'Next day'}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={handleToday}
            className={cn(
              'shrink-0 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
              colors.info,
              colors.interactiveHover
            )}
          >
            Today
          </button>
        </div>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none ml-4">
          <Switch
            checked={showOnlyOpen}
            onChange={(e) => setShowOnlyOpen(e.target.checked)}
            size="sm"
            aria-label="Show only open todos"
          />
          <span className={cn('text-sm font-medium whitespace-nowrap', colors.text)}>Open only</span>
        </label>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none ml-2">
          <Switch
            checked={showAppointments}
            onChange={(e) => setShowAppointments(e.target.checked)}
            size="sm"
            aria-label="Show appointments in calendar"
          />
          <span className={cn('text-sm font-medium whitespace-nowrap', colors.text)}>Show appointments</span>
        </label>
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <button
            onClick={() => navigate('/todos/new')}
            className={cn(
              'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-all hover:opacity-90',
              colors.primary,
              colors.primaryText,
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400/50 dark:focus:ring-gray-500/50 dark:focus:ring-offset-gray-900'
            )}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Todo
          </button>
          <button
            onClick={() => navigate('/appointments/new')}
            className={cn(
              'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-all hover:opacity-90',
              colors.primary,
              colors.primaryText,
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400/50 dark:focus:ring-gray-500/50 dark:focus:ring-offset-gray-900'
            )}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Appointment
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {viewMode === 'month' && (
          <TodosMonthlyView
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            showOnlyOpen={showOnlyOpen}
            showAppointments={showAppointments}
            setShowAppointments={setShowAppointments}
            embeddedInCalendar
          />
        )}
        {viewMode === 'week' && (
          <TodosWeeklyView
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            showOnlyOpen={showOnlyOpen}
            showAppointments={showAppointments}
            setShowAppointments={setShowAppointments}
            embeddedInCalendar
          />
        )}
        {viewMode === 'day' && (
          <TodosDailyView
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            showOnlyOpen={showOnlyOpen}
            showAppointments={showAppointments}
            setShowAppointments={setShowAppointments}
            embeddedInCalendar
          />
        )}
      </div>
    </div>
  );
};

export default TodosCalendarView;
